/**
 * Question Item Component
 * Single Responsibility: Renders a single question item with click handling
 *
 * Best Practice: Uses regular <img> tag for SVG icons instead of Next.js Image
 * because SVGs don't need optimization and load faster from browser cache
 */

import React from 'react';
import {
  typographyColors,
  typography,
  spacing,
  borders,
} from '@/design-system/utils';

export interface QuestionItemProps {
  question: string;
  onClick?: (question: string) => void;
  disabled?: boolean;
}

/**
 * Question Item Component
 * Displays a single question with optional click handler
 */
export const QuestionItem: React.FC<QuestionItemProps> = ({
  question,
  onClick,
  disabled = false,
}) => {
  return (
    <button
      onClick={() => onClick?.(question)}
      disabled={disabled}
      style={{
        display: 'flex',
        padding: `${spacing[2.5]} ${spacing[2]}`,
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: spacing[2],
        alignSelf: 'stretch',
        borderRadius: borders.radius.sm,
        border: 'none',
        background: 'transparent',
        cursor: onClick && !disabled ? 'pointer' : 'default',
        transition: 'opacity 0.2s',
        outline: 'none',
        width: '100%',
      }}
      onMouseEnter={e => {
        if (onClick && !disabled) {
          e.currentTarget.style.opacity = '0.8';
        }
      }}
      onMouseLeave={e => {
        if (onClick && !disabled) {
          e.currentTarget.style.opacity = '1';
        }
      }}
    >
      <div
        style={{
          width: '20px',
          height: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {/* Using regular img tag for SVG icons (best practice) */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src='/assets/images/icons/chat_round.svg'
          alt='Chat'
          width={20}
          height={20}
        />
      </div>
      <span
        style={{
          color: typographyColors.text,
          textAlign: 'left',
          fontFamily: typography.fonts.primary,
          fontSize: typography.size.sm,
          fontStyle: 'normal',
          fontWeight: typography.weight.regular,
          lineHeight: 'normal',
          flex: 1,
        }}
      >
        {question}
      </span>
    </button>
  );
};
